import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {z as r} from 'zod';

import {LocaleNameEnum} from '../../provider/locale/locale-context-type';
import {Locale} from '../../provider/locale/localization';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {fetchAndDeserialize, postAndDeserialize, serialize} from '../../util/api-adapter';
import {ApiError, HttpError} from '../../util/error';
import {FetchMethodEnum, fetchNoContent, fetchX} from '../../util/fetch';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../api/api-const';

const reviewStatusSchema = r.object({
    statusCode: r.string(),
    title: r.string(),
    isDefault: r.boolean(),
    canDelete: r.boolean().optional(),
});

export type ReviewStatusType = r.infer<typeof reviewStatusSchema>;

const reviewStatusListResponseSchema = r.object({
    statuses: r.array(reviewStatusSchema),
});

type ReviewStatusListResponseType = r.infer<typeof reviewStatusListResponseSchema>;

type UpdateReviewStatusType = {
    status: string;
};

type CreateReviewStatusType = {
    title: string;
};

type ManageReviewStatusErrorPayloadType = {
    status: string;
};

export class ManageReviewStatusError extends ApiError {
    name = 'ManageReviewStatusError';
    jsonData: ManageReviewStatusErrorPayloadType;

    constructor(message: string, statusCode: number, jsonData: ManageReviewStatusErrorPayloadType) {
        super(message, statusCode, jsonData);

        this.jsonData = jsonData;
    }
}

function isManageReviewStatusErrorPayloadType(
    errorPayload: unknown
): errorPayload is ManageReviewStatusErrorPayloadType {
    return typeof (errorPayload as ManageReviewStatusErrorPayloadType)?.status === 'string';
}

function handleManageReviewStatusError(error: unknown): never {
    if (error instanceof ApiError && isManageReviewStatusErrorPayloadType(error.jsonData)) {
        throw new ManageReviewStatusError(error.message, error.statusCode, error.jsonData);
    }

    throw error;
}

const reviewStatusListUrl = '/v4/reviews/statuses/';

async function fetchReviewStatusList(): Promise<ReviewStatusListResponseType> {
    return fetchAndDeserialize<ReviewStatusListResponseType>(reviewStatusListUrl, reviewStatusListResponseSchema);
}

export function useReviewStatusList(localeName: LocaleNameEnum): UseQueryResult<ReviewStatusListResponseType> {
    return useQuery([reviewStatusListUrl + localeName], () => fetchReviewStatusList(), {
        refetchOnWindowFocus: false,
    });
}

export function updateReviewStatus(id: number, payload: UpdateReviewStatusType): Promise<void> {
    const url = `${rootApiUrl}/v4/reviews/${id}/status/`;
    const body = JSON.stringify(serialize<UpdateReviewStatusType>(payload));

    return fetchX<void>(url, {
        body,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        method: FetchMethodEnum.patch,
    }).catch(handleManageReviewStatusError);
}

async function createReviewStatus(payload: CreateReviewStatusType): Promise<ReviewStatusType> {
    return postAndDeserialize<ReviewStatusType>(reviewStatusListUrl, reviewStatusSchema, payload);
}

function deleteReviewStatus(statusCode: string, defaultStatusCode: string): Promise<void> {
    const url = `${rootApiUrl}${reviewStatusListUrl}${statusCode}/`;
    const body = JSON.stringify({status_code: defaultStatusCode});

    return fetchNoContent(url, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body,
    }).catch(handleManageReviewStatusError);
}

export function useCreateReviewStatus(
    localeName: LocaleNameEnum
): UseMutationResult<ReviewStatusType, unknown, CreateReviewStatusType> {
    const {snackbar} = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            return createReviewStatus(payload);
        },
        onSuccess: async (createdStatus: ReviewStatusType) => {
            await queryClient.invalidateQueries({
                queryKey: [reviewStatusListUrl + localeName],
            });

            snackbar.success(<Locale stringKey="REVIEWS__STATUS__CREATE_SUCCESS" />);

            return createdStatus;
        },
        onError: (error: unknown) => {
            const description =
                error instanceof ManageReviewStatusError ? (
                    <span>{error.jsonData.status}</span>
                ) : (
                    <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />
                );

            snackbar.error({
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                description,
            });

            if (!(error instanceof HttpError)) {
                throw error;
            }
        },
    });
}

type DeleteReviewStatusPayloadType = {
    statusCode: string;
    replacementStatusCode: string;
};

export function useDeleteReviewStatus(
    localeName: LocaleNameEnum
): UseMutationResult<void, unknown, DeleteReviewStatusPayloadType> {
    const {snackbar} = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: DeleteReviewStatusPayloadType) => {
            return deleteReviewStatus(payload.statusCode, payload.replacementStatusCode);
        },
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: [reviewStatusListUrl + localeName],
            });

            snackbar.success(<Locale stringKey="REVIEWS__STATUS__DELETE_SUCCESS" />);
        },
        onError: (error: unknown) => {
            const description =
                error instanceof ManageReviewStatusError ? (
                    <span>{error.jsonData.status}</span>
                ) : (
                    <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />
                );

            snackbar.error({
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                description,
            });

            if (!(error instanceof HttpError)) {
                throw error;
            }
        },
    });
}
